import {
  Text, Stack, SimpleGrid, Skeleton, Paper, Table,
} from '@mantine/core';
import { IconReceipt } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import { useCashForecast } from '@/hooks/usePortalApi';

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

export default function CashForecast() {
  const { data, isLoading } = useCashForecast();

  return (
    <Stack gap="lg">
      <PageHeader title="Cash Forecast" description="Projected cash flow and balances" />
      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {[...Array(3)].map((_, i) => <Skeleton key={i} height={120} radius="md" />)}
        </SimpleGrid>
      ) : !data ? (
        <EmptyState icon={<IconReceipt size={30} />} title="No forecast data" description="No cash forecast data available" />
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            <StatCard
              title="Current Balance"
              value={formatCurrency(data.current_balance ?? 0)}
              icon={<IconReceipt size={24} />}
            />
            <StatCard
              title="Projected Inflows"
              value={formatCurrency(data.projected_inflows ?? 0)}
              icon={<IconReceipt size={24} />}
            />
            <StatCard
              title="Projected Outflows"
              value={formatCurrency(data.projected_outflows ?? 0)}
              icon={<IconReceipt size={24} />}
            />
          </SimpleGrid>

          {data.periods && data.periods.length > 0 && (
            <Paper>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Period</Table.Th>
                    <Table.Th>Inflows</Table.Th>
                    <Table.Th>Outflows</Table.Th>
                    <Table.Th>Net</Table.Th>
                    <Table.Th>Ending Balance</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.periods.map((period: any, index: number) => (
                    <Table.Tr key={index}>
                      <Table.Td><Text size="sm" fw={500}>{period.period ?? period.label}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" c="green">{formatCurrency(period.inflows ?? 0)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" c="red">{formatCurrency(period.outflows ?? 0)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" fw={500}>{formatCurrency(period.net ?? 0)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" fw={500}>{formatCurrency(period.ending_balance ?? 0)}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}
